import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, Alert, SafeAreaView,
  ScrollView, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import { rideService } from '../../services/rideService';

const ProfileScreen = ({ navigation }) => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await rideService.getProfile();
      if (response.success) {
        setProfile(response.data.user);
        setName(response.data.user.name);
        setPhone(response.data.user.phone || '');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Use auth context user as fallback
      setName(user?.name || '');
      setPhone(user?.phone || '');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await rideService.updateProfile({ name, phone });
      if (response.success) {
        setProfile(response.data.user);
        setEditing(false);
        Alert.alert('Success', 'Profile updated');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to update profile');
    }
  };

  const handleLogout = () => {
    Alert.alert('Logout', 'Are you sure you want to logout?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Logout', style: 'destructive', onPress: logout },
    ]);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <ActivityIndicator size="large" color="#000" style={{ flex: 1 }} />
      </SafeAreaView>
    );
  }

  const displayUser = profile || user;

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.avatarContainer}>
          <View style={styles.avatar}>
            <Ionicons name="person" size={48} color="#666" />
          </View>
          <Text style={styles.userName}>{displayUser?.name}</Text>
          <Text style={styles.userEmail}>{displayUser?.email}</Text>
          <View style={styles.roleBadge}>
            <Text style={styles.roleText}>{displayUser?.role}</Text>
          </View>
        </View>

        {editing ? (
          <View style={styles.editSection}>
            <Text style={styles.inputLabel}>Name</Text>
            <TextInput style={styles.input} value={name} onChangeText={setName} />
            <Text style={styles.inputLabel}>Phone</Text>
            <TextInput style={styles.input} value={phone} onChangeText={setPhone} keyboardType="phone-pad" />
            <View style={styles.editButtons}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditing(false)}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
                <Text style={styles.saveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={styles.menuSection}>
            <TouchableOpacity style={styles.menuItem} onPress={() => setEditing(true)}>
              <Ionicons name="create-outline" size={24} color="#333" />
              <Text style={styles.menuText}>Edit Profile</Text>
              <Ionicons name="chevron-forward" size={20} color="#ccc" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.menuItem} onPress={() => navigation.navigate('RideHistory')}>
              <Ionicons name="time-outline" size={24} color="#333" />
              <Text style={styles.menuText}>Ride History</Text>
              <Ionicons name="chevron-forward" size={20} color="#ccc" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.menuItem} onPress={() => Alert.alert('Coming Soon', 'Payment methods will be available soon.')}>
              <Ionicons name="card-outline" size={24} color="#333" />
              <Text style={styles.menuText}>Payment Methods</Text>
              <Ionicons name="chevron-forward" size={20} color="#ccc" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.menuItem} onPress={() => Alert.alert('Coming Soon', 'Notifications settings will be available soon.')}>
              <Ionicons name="notifications-outline" size={24} color="#333" />
              <Text style={styles.menuText}>Notifications</Text>
              <Ionicons name="chevron-forward" size={20} color="#ccc" />
            </TouchableOpacity>

            <TouchableOpacity style={[styles.menuItem, styles.logoutItem]} onPress={handleLogout}>
              <Ionicons name="log-out-outline" size={24} color="#F44336" />
              <Text style={[styles.menuText, { color: '#F44336' }]}>Logout</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { padding: 20 },
  avatarContainer: { alignItems: 'center', marginBottom: 30, paddingTop: 20 },
  avatar: {
    width: 100, height: 100, borderRadius: 50, backgroundColor: '#E0E0E0',
    justifyContent: 'center', alignItems: 'center', marginBottom: 15,
  },
  userName: { fontSize: 24, fontWeight: 'bold', color: '#333' },
  userEmail: { fontSize: 16, color: '#666', marginTop: 4 },
  roleBadge: { backgroundColor: '#000', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, marginTop: 8 },
  roleText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  menuSection: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  menuItem: {
    flexDirection: 'row', alignItems: 'center', padding: 16,
    borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
  },
  menuText: { flex: 1, fontSize: 16, color: '#333', marginLeft: 15 },
  logoutItem: { borderBottomWidth: 0 },
  editSection: { backgroundColor: '#fff', borderRadius: 12, padding: 20 },
  inputLabel: { fontSize: 14, color: '#666', marginBottom: 5, marginTop: 10 },
  input: {
    borderWidth: 1, borderColor: '#E0E0E0', borderRadius: 8,
    padding: 12, fontSize: 16, backgroundColor: '#FAFAFA',
  },
  editButtons: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 20 },
  cancelBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 8, borderWidth: 1, borderColor: '#E0E0E0', marginRight: 10 },
  cancelBtnText: { fontSize: 16, color: '#666' },
  saveBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 8, backgroundColor: '#000', marginLeft: 10 },
  saveBtnText: { fontSize: 16, color: '#fff', fontWeight: 'bold' },
});

export default ProfileScreen;
